<template>
    <div class="flex justify-between items-center w-full bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-4 py-3 shadow-md">

        <div class="flex flex-1 items-center gap-3 min-w-0">
            <div class="relative group flex-shrink-0">
                <div class="absolute inset-0 bg-white/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img 
                    v-if="user?.profil" 
                    :src="user.profil" 
                    class="relative w-10 h-10 rounded-full object-cover border-2 border-white/30 shadow-lg transition-transform duration-200 group-hover:scale-105" 
                    alt="Profile"
                >
                <div 
                    v-else 
                    class="relative w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/30 shadow-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                >
                    <i class="las la-user text-xl text-white"></i>
                </div>
                <!-- Badge en ligne -->
                <div class="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-blue-600 dark:border-blue-700 rounded-full"></div>
            </div>
            
            <div class="flex flex-col min-w-0 flex-1">
                <b class="text-sm font-bold text-white line-clamp-1 drop-shadow-sm">
                    {{ user?.lastname }} {{ user?.firstname }}
                </b>
                <a 
                    :href="`mailto:${user?.email}`" 
                    class="text-xs text-blue-100 hover:text-white line-clamp-1 transition-colors duration-200 flex items-center gap-1"
                >
                    <i class="las la-envelope text-sm"></i>
                    <span class="truncate">{{ user?.email }}</span>
                </a>
            </div>
        </div>

        <div class="flex justify-end items-center gap-2 flex-shrink-0">
            <!-- Desktop buttons -->
            <div class="hidden lg:flex gap-2">
                <button
                    @click="setTheme"
                    :class="[
                        'group relative w-9 h-9 rounded-full backdrop-blur-sm transition-all duration-200 flex items-center justify-center',
                        'bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30',
                        'hover:scale-110 active:scale-95'
                    ]"
                    :title="isDark === 'light' ? 'Mode sombre' : 'Mode clair'"
                >
                    <div class="absolute inset-0 bg-white/5 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <i 
                        v-if="isDark === 'light'" 
                        class="las la-moon text-lg text-white relative z-10"
                    ></i>
                    <i 
                        v-else 
                        class="las la-sun text-lg text-white relative z-10"
                    ></i>
                </button>

                <button
                    @click="logoutUser"
                    class="group relative w-9 h-9 rounded-full backdrop-blur-sm transition-all duration-200 flex items-center justify-center bg-white/10 hover:bg-red-500/80 border border-white/20 hover:border-red-400/50 hover:scale-110 active:scale-95"
                    title="Déconnexion"
                >
                    <div class="absolute inset-0 bg-red-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <i class="las la-sign-out-alt text-lg text-white relative z-10"></i>
                </button>
            </div>

            <!-- Mobile menu button -->
            <div class="relative block lg:hidden">
                <button
                    @click.stop="toggleDropMenu"
                    :class="[
                        'w-9 h-9 rounded-full backdrop-blur-sm transition-all duration-200 flex items-center justify-center',
                        'bg-white/10 hover:bg-white/20 border border-white/20',
                        showDropMenu ? 'bg-white/20 scale-95' : 'hover:scale-110'
                    ]"
                >
                    <i 
                        :class="[
                            'text-lg text-white transition-transform duration-200',
                            showDropMenu ? 'las la-times rotate-90' : 'las la-ellipsis-v'
                        ]"
                    ></i>
                </button>

                <!-- Dropdown menu -->
                <transition
                    enter-active-class="transition duration-200 ease-out"
                    enter-from-class="opacity-0 scale-95 -translate-y-2"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-active-class="transition duration-150 ease-in"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 -translate-y-2"
                >
                    <div 
                        v-if="showDropMenu"
                        class="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
                    >
                        <div class="p-1.5">
                            <!-- Theme toggle -->
                            <button
                                @click.stop="setTheme"
                                class="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 group"
                            >
                                <div class="flex items-center gap-2.5">
                                    <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                        <i 
                                            v-if="isDark === 'light'" 
                                            class="las la-moon text-base text-blue-600 dark:text-blue-400"
                                        ></i>
                                        <i 
                                            v-else 
                                            class="las la-sun text-base text-yellow-600 dark:text-yellow-400"
                                        ></i>
                                    </div>
                                    <span>{{ isDark === 'light' ? 'Mode sombre' : 'Mode clair' }}</span>
                                </div>
                                <i class="las la-chevron-right text-xs text-gray-400 group-hover:translate-x-1 transition-transform duration-200"></i>
                            </button>

                            <!-- Divider -->
                            <div class="h-px bg-gray-200 dark:bg-gray-700 my-1.5"></div>

                            <!-- Logout -->
                            <button
                                @click="logoutUser"
                                class="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 group"
                            >
                                <div class="flex items-center gap-2.5">
                                    <div class="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                        <i class="las la-sign-out-alt text-base text-red-600 dark:text-red-400"></i>
                                    </div>
                                    <span>Déconnexion</span>
                                </div>
                                <i class="las la-chevron-right text-xs text-red-400 group-hover:translate-x-1 transition-transform duration-200"></i>
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions } from 'vuex';

export default {
    name: "InboxInfoUser",
    
    data() {
        return {
            isDark: null,
            showDropMenu: false
        }
    },

    computed: {
        ...mapFields("auth/user", {
            user: "item",
            isLoading: "isLoading",
            isSuccess: "isSuccess",
            showMessage: "showMessage",
            message: "message",
        })
    },

    methods: {
        ...mapActions({
            logoutUser: "auth/user/logoutUser"
        }),

        setTheme() {
            this.isDark = this.isDark === "light" ? "dark" : "light";
        },

        toggleDropMenu() {
            this.showDropMenu = !this.showDropMenu;
        },

        closeDropMenu() {
            this.showDropMenu = false;
        }
    },

    watch: {
        isDark(val) {
            if (val === "light") {
                document.documentElement.classList.remove("dark");
            } else {
                document.documentElement.classList.add("dark");
            }
            localStorage.setItem("theme", val ?? "dark");
        }
    },

    mounted() {
        // Close dropdown when clicking outside
        document.addEventListener('click', this.closeDropMenu);

        // Initialize theme
        if (!localStorage.getItem("theme")) {
            localStorage.setItem("theme", "light");
            this.isDark = 'light';
        } else {
            this.isDark = localStorage.getItem("theme");
        }
    },

    beforeUnmount() {
        document.removeEventListener('click', this.closeDropMenu);
    }
}
</script>

<style scoped>
/* Smooth transitions pour tous les éléments interactifs */
button, a {
    @apply transition-all duration-200;
}

/* Animation du badge en ligne */
@keyframes pulse-ring {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

.animate-pulse-ring {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>